import React, { useEffect, useState } from "react";
import "./faqs.scss";
import CommonHeader from "../../components/CommonHeader";
import faqIcon from "../../assets/images/faq-icon.svg";
import useFaqs from "../../Hooks/useFaqs";
import { useAppSelector } from "../../redux/hooks";
import { userGuides } from "../../redux/faqs/faqsSlice";
import Loading from "../../components/LoadingPage/Loading";

const RecursiveAccordion = ({
  guides,
  highlightText,
  depth = 0,
}: {
  guides: any[];
  highlightText: (text: string) => string;
  depth?: number;
}) => {
  if (!guides || guides.length === 0) return null;

  return (
    <div className={`accordion ${depth > 0 ? "nested-accordion" : ""}`}>
      {guides.map((guide: any) => {
        const hasChildren =
          guide?.childUserGuides && guide.childUserGuides.length > 0;

        return (
          <div className="accordion-item" key={guide?.id}>
            <h2 className="accordion-header">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#guide-${guide?.id}`}
                aria-expanded="false"
                aria-controls={`guide-${guide?.id}`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: highlightText(guide?.title || ""),
                  }}
                ></div>
              </button>
            </h2>
            <div
              id={`guide-${guide?.id}`}
              className="accordion-collapse collapse"
            >
              <div className="accordion-body">
                {guide?.description && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightText(guide.description),
                    }}
                  />
                )}
                {hasChildren && (
                  <RecursiveAccordion
                    guides={guide.childUserGuides}
                    highlightText={highlightText}
                    depth={depth + 1}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const filterGuidesRecursive = (
  guides: any[],
  lowerSearch: string
): any[] => {
  return guides
    .map((guide: any) => {
      const titleMatch = guide?.title?.toLowerCase()?.includes(lowerSearch);
      const descMatch = guide?.description
        ?.toLowerCase()
        ?.includes(lowerSearch);
      const filteredChildren = guide?.childUserGuides
        ? filterGuidesRecursive(guide.childUserGuides, lowerSearch)
        : [];

      if (titleMatch || descMatch || filteredChildren.length > 0) {
        return { ...guide, childUserGuides: filteredChildren };
      }
      return null;
    })
    .filter(Boolean);
};

const Faq = () => {
  const userGuidesData = useAppSelector(userGuides);
  const [searchVal, setSearchVal] = useState<string>("");
  const [faqData, setFaqData] = useState<any[]>([]);

  const { getUserGuidesAPI, getUserGuidesLoading } = useFaqs();

  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightText = (text: string) => {
    if (!searchVal) return text;
    const escapedSearchVal = escapeRegExp(searchVal);
    const regex = new RegExp(`(${escapedSearchVal})`, "gi");
    return text.replace(regex, `<mark>$1</mark>`);
  };

  useEffect(() => {
    const sections = userGuidesData?.childUserGuides || [];

    if (!searchVal) {
      setFaqData(sections);
    } else {
      const lowerSearch = searchVal.toLowerCase();
      const filteredData = filterGuidesRecursive(sections, lowerSearch);
      setFaqData(filteredData);
    }
  }, [searchVal, userGuidesData]);

  useEffect(() => {
    getUserGuidesAPI().catch((err) =>
      console.error("Failed to load User Guides", err)
    );
  }, []);

  return (
    <div className="faqs-wrapper">
      <CommonHeader
        pageTitle={userGuidesData?.title || "FAQs"}
        pageSubtitle={userGuidesData?.description || "Search here to get answers to your questions"}
        searchValue={searchVal}
        handleSearchChange={setSearchVal}
      />

      {getUserGuidesLoading ? (
        <Loading />
      ) : (
        <div className="faq-list-wrapper">
          {faqData?.length > 0 ? (
            faqData.map((section: any) => (
              <div className="faq-section" key={section?.id}>
                <div className="faq-section-title">
                  <img
                    src={faqIcon}
                    alt={section?.title}
                    height={32}
                    width={32}
                  />
                  <h4>{section?.title}</h4>
                </div>
                <RecursiveAccordion
                  guides={section?.childUserGuides || []}
                  highlightText={highlightText}
                />
              </div>
            ))
          ) : (
            <p className="no-results">
              No Documents found {searchVal ? `for "${searchVal}"` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Faq;